import type { FC, ReactNode } from 'react'
import type { Area } from '@/lib/stretching'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
    Info,
    Pause,
    Play,
    Printer,
    SkipForward,
    Square,
    StretchHorizontal
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { printArea, removePrintFrames } from '@/lib/print-area'

import {
    AREAS,
    HOLD_SECONDS,
    MINUTE_OPTIONS,
    POSITION_LABELS,
    SETS,
    buildRoutine,
    routineSteps,
    stretchSeconds
} from '@/lib/stretching'

const chipClass = (selected: boolean): string =>
    cn(
        'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const buttonClass =
    'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

export const StretchingRoutineBuilder: FC = (): ReactNode => {
    const [areas, setAreas] = useState<Area[]>(['neck', 'back', 'legs'])
    const [minutes, setMinutes] = useState(10)
    const [noFloor, setNoFloor] = useState(false)

    const [stepIndex, setStepIndex] = useState<number | null>(null)
    const [remaining, setRemaining] = useState(HOLD_SECONDS)
    const [running, setRunning] = useState(false)
    const [sound, setSound] = useState(true)

    const audioRef = useRef<AudioContext | null>(null)
    const printRef = useRef<HTMLDivElement>(null)

    const routine = buildRoutine({ areas, minutes, noFloor })
    const steps = routineSteps(routine)
    const routineKey = `${areas.join(',')}|${minutes}|${noFloor}`
    const totalMinutes = Math.max(1, Math.round(routine.reduce((sum, stretch) => sum + stretchSeconds(stretch), 0) / 60))

    // A soft tone when a hold starts. The audio context is created on the
    // first tap of Start, because browsers only allow sound after a gesture.
    const chime = (): void => {
        const context = audioRef.current
        if (!sound || !context) return
        const oscillator = context.createOscillator()
        const gain = context.createGain()
        oscillator.frequency.value = 660
        gain.gain.setValueAtTime(0.0001, context.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.15, context.currentTime + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.6)
        oscillator.connect(gain).connect(context.destination)
        oscillator.start()
        oscillator.stop(context.currentTime + 0.65)
    }

    const stop = (): void => {
        setRunning(false)
        setStepIndex(null)
        setRemaining(HOLD_SECONDS)
    }

    // Changing the routine ends any session in progress.
    useEffect(() => {
        stop()
    }, [routineKey])

    useEffect(() => {
        return () => {
            removePrintFrames()
            audioRef.current?.close().catch(() => undefined)
        }
    }, [])

    useEffect(() => {
        if (!running) return
        const timer = window.setInterval(() => setRemaining((value) => value - 1), 1000)
        return () => window.clearInterval(timer)
    }, [running])

    useEffect(() => {
        if (stepIndex === null || remaining > 0) return
        if (stepIndex + 1 >= steps.length) {
            chime()
            setRunning(false)
            setStepIndex(steps.length)
            return
        }
        setStepIndex(stepIndex + 1)
        setRemaining(HOLD_SECONDS)
        chime()
    }, [remaining])

    const start = (): void => {
        if (!audioRef.current && typeof window.AudioContext !== 'undefined') {
            audioRef.current = new window.AudioContext()
        }
        audioRef.current?.resume().catch(() => undefined)

        if (stepIndex === null || stepIndex >= steps.length) {
            setStepIndex(0)
            setRemaining(HOLD_SECONDS)
            chime()
        }
        setRunning(true)
    }

    const skip = (): void => {
        if (stepIndex === null) return
        setRemaining(0)
    }

    const toggleArea = (area: Area): void => {
        setAreas((current) =>
            current.includes(area) ? current.filter((item) => item !== area) : AREAS.map((item) => item.value).filter((value) => value === area || current.includes(value))
        )
    }

    const current = stepIndex !== null && stepIndex < steps.length ? steps[stepIndex] : null
    const next = stepIndex !== null && stepIndex + 1 < steps.length ? steps[stepIndex + 1] : null
    const finished = stepIndex !== null && stepIndex >= steps.length

    return (
        <section
            aria-label='Stretching routine builder'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <StretchHorizontal className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Stretching routine builder
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Choose the areas that feel tight and how long you have, then follow along with the timer.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <span id='stretch-areas-label' className='block text-xs font-medium text-muted-foreground'>
                    Areas to stretch
                </span>
                <div role='group' aria-labelledby='stretch-areas-label' className='mt-2 flex flex-wrap gap-2'>
                    {AREAS.map((area) => (
                        <button
                            key={area.value}
                            type='button'
                            aria-pressed={areas.includes(area.value)}
                            onClick={() => toggleArea(area.value)}
                            className={chipClass(areas.includes(area.value))}
                        >
                            {area.label}
                        </button>
                    ))}
                </div>

                <div className='mt-5 flex flex-wrap items-end gap-x-6 gap-y-4'>
                    <div>
                        <span id='stretch-minutes-label' className='block text-xs font-medium text-muted-foreground'>
                            Time you have
                        </span>
                        <div role='radiogroup' aria-labelledby='stretch-minutes-label' className='mt-2 flex flex-wrap gap-2'>
                            {MINUTE_OPTIONS.map((option) => (
                                <button
                                    key={option}
                                    type='button'
                                    role='radio'
                                    aria-checked={minutes === option}
                                    onClick={() => setMinutes(option)}
                                    className={chipClass(minutes === option)}
                                >
                                    {option} minutes
                                </button>
                            ))}
                        </div>
                    </div>
                    <label className='flex cursor-pointer items-center gap-2 text-sm text-foreground'>
                        <input
                            type='checkbox'
                            checked={noFloor}
                            onChange={(event) => setNoFloor(event.target.checked)}
                            className='size-4 accent-primary'
                        />
                        Standing and chair stretches only
                    </label>
                </div>

                <div aria-live='polite' className='mt-6'>
                    {routine.length === 0 ? (
                        <span className='block rounded-xl border border-border bg-background px-4 py-4 text-sm text-muted-foreground'>
                            Choose at least one area to build your routine.
                        </span>
                    ) : (
                        <div>
                            <div className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                {current || finished ? (
                                    <div>
                                        <span className='block text-sm font-medium text-foreground'>
                                            {finished
                                                ? 'Routine complete'
                                                : `Stretch ${current!.stretchIndex + 1} of ${routine.length}: ${current!.stretch.name}`}
                                        </span>
                                        <span className='mt-1 block font-heading text-5xl font-semibold tabular-nums tracking-tight text-foreground'>
                                            {finished ? 'Well done' : `0:${String(Math.max(0, remaining)).padStart(2, '0')}`}
                                        </span>
                                        <span className='mt-1 block text-sm text-muted-foreground'>
                                            {finished
                                                ? 'Breathe normally and take a moment before you carry on with your day.'
                                                : `${current!.label}. ${next ? `Next: ${next.stretch.name === current!.stretch.name ? next.label : next.stretch.name}.` : 'Last hold.'}`}
                                        </span>
                                    </div>
                                ) : (
                                    <div>
                                        <span className='block text-sm font-medium text-foreground'>Your routine</span>
                                        <span className='mt-1 block font-heading text-3xl font-semibold tracking-tight text-foreground'>
                                            {routine.length} {routine.length === 1 ? 'stretch' : 'stretches'}, about {totalMinutes} minutes
                                        </span>
                                        <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                                            Hold each one for {HOLD_SECONDS} seconds, {SETS} times (each side where it has two), easing in
                                            to a gentle pull, never pain.
                                        </span>
                                    </div>
                                )}

                                <span className='mt-4 flex flex-wrap items-center gap-3'>
                                    {running ? (
                                        <button type='button' onClick={() => setRunning(false)} className={cn(buttonClass, 'bg-primary text-primary-foreground hover:opacity-90')}>
                                            <Pause className='size-4' />
                                            Pause
                                        </button>
                                    ) : (
                                        <button type='button' onClick={start} className={cn(buttonClass, 'bg-primary text-primary-foreground hover:opacity-90')}>
                                            <Play className='size-4' />
                                            {current ? 'Resume' : finished ? 'Start again' : 'Start with timer'}
                                        </button>
                                    )}
                                    {current && (
                                        <button type='button' onClick={skip} className={cn(buttonClass, 'border border-border bg-background text-foreground hover:bg-secondary')}>
                                            <SkipForward className='size-4' />
                                            Skip
                                        </button>
                                    )}
                                    {(current || finished) && (
                                        <button type='button' onClick={stop} className={cn(buttonClass, 'border border-border bg-background text-foreground hover:bg-secondary')}>
                                            <Square className='size-4' />
                                            Stop
                                        </button>
                                    )}
                                    <label className='flex cursor-pointer items-center gap-2 text-sm text-foreground'>
                                        <input
                                            type='checkbox'
                                            checked={sound}
                                            onChange={(event) => setSound(event.target.checked)}
                                            className='size-4 accent-primary'
                                        />
                                        Tone at each change
                                    </label>
                                </span>
                            </div>

                            <div data-print-area ref={printRef} className='mt-5'>
                                <span className='mb-4 hidden text-sm text-muted-foreground print:block'>
                                    Made with the free stretching routine builder from WellPath Fusion.
                                </span>
                                <span className='grid gap-3 sm:grid-cols-2'>
                                    {routine.map((stretch, index) => (
                                        <span
                                            key={stretch.id}
                                            className={cn(
                                                'block break-inside-avoid rounded-xl border bg-background px-4 py-4',
                                                current?.stretchIndex === index ? 'border-primary ring-1 ring-primary' : 'border-border'
                                            )}
                                        >
                                            <span className='flex items-baseline justify-between gap-2'>
                                                <span className='font-heading text-base font-semibold text-foreground'>
                                                    {index + 1}. {stretch.name}
                                                </span>
                                                <span className='shrink-0 text-xs text-muted-foreground'>{POSITION_LABELS[stretch.position]}</span>
                                            </span>
                                            <span className='mt-1 block text-xs text-muted-foreground'>
                                                Feel it: {stretch.feel.toLowerCase()}. {HOLD_SECONDS} seconds, {SETS} times{stretch.sided ? ' each side' : ''}.
                                            </span>
                                            <span className='mt-3 flex flex-col gap-1.5'>
                                                {stretch.steps.map((step, stepNumber) => (
                                                    <span key={step} className='flex gap-2 text-sm leading-relaxed text-foreground'>
                                                        <span className='w-4 shrink-0 text-xs tabular-nums text-muted-foreground'>{stepNumber + 1}</span>
                                                        <span>{step}</span>
                                                    </span>
                                                ))}
                                            </span>
                                        </span>
                                    ))}
                                </span>
                                <span className='mt-4 hidden text-xs leading-relaxed text-muted-foreground print:block'>
                                    Stretch to a gentle pull, never pain, and do not bounce. General guidance, not medical advice.
                                </span>
                            </div>

                            <span className='mt-5 flex flex-wrap gap-3 print:hidden'>
                                <button
                                    type='button'
                                    onClick={() => printRef.current && printArea(printRef.current, 'Your stretching routine | WellPath Fusion')}
                                    className={cn(buttonClass, 'border border-border bg-background text-foreground hover:bg-secondary')}
                                >
                                    <Printer className='size-4' />
                                    Print or save as PDF
                                </button>
                            </span>
                        </div>
                    )}
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        Stretch when your muscles are warm, ease in slowly and stop if you feel sharp pain, numbness or
                        tingling. If you have had recent surgery or an injury, or have joint or back problems, check with a
                        doctor or physiotherapist first. Nothing you choose is stored. For back pain, see{' '}
                        <Link href='/movement/lower-back-pain-stretches-exercises' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            stretches for lower back pain
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default StretchingRoutineBuilder
